/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NB_AUTH_OPTIONS, NbAuthJWTToken, NbTokenStorage } from '@nebular/auth';
import { NbGlobalLogicalPosition, NbToastrService } from '@nebular/theme';
import { getDeepFromObject } from '../../../helpers';
import { UserData } from '../../../../@core/interfaces/auth/users';
import { UserStore } from '../../../../@core/stores/user.store';

@Component({
  selector: 'ngx-change-password-form',
  styleUrls: ['./change-password-form.component.scss'],
  templateUrl: './change-password-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgxChangePasswordFormComponent implements OnInit {
  minLength: number = this.getConfigValue('forms.validation.password.minLength');
  maxLength: number = this.getConfigValue('forms.validation.password.maxLength');
  redirectDelay: number = this.getConfigValue('forms.resetPassword.redirectDelay');
  isPasswordRequired: boolean = this.getConfigValue('forms.validation.password.required');

  submitted = false;
  errors: string[] = [];
  messages: string[] = [];

  changePasswordForm: FormGroup;

  constructor(
    @Inject(NB_AUTH_OPTIONS) protected options = {},
    protected cd: ChangeDetectorRef,
    protected fb: FormBuilder,
    protected router: Router,
    private api: UserData,
    private userStore: UserStore,
    private tokenStorage: NbTokenStorage,
    private toast: NbToastrService,
  ) { }

  ngOnInit(): void {
    const passwordValidators = [
      Validators.minLength(this.minLength),
      Validators.maxLength(this.maxLength),
    ];
    this.isPasswordRequired && passwordValidators.push(Validators.required);

    this.changePasswordForm = this.fb.group({
      currentPassword: this.fb.control('', [Validators.required]),
      password: this.fb.control('', [...passwordValidators]),
      passwordConfirmation: this.fb.control('', [...passwordValidators]),
    });
  }

  get currentPassword() { return this.changePasswordForm.get('currentPassword'); }
  get password() { return this.changePasswordForm.get('password'); }
  get passwordConfirmation() { return this.changePasswordForm.get('passwordConfirmation'); }

  changePassword(): void {
    this.errors = this.messages = [];

    if (this.password.value !== this.passwordConfirmation.value) {
      this.errors = ['Password and confirm password does not match!'];
      return;
    }

    this.submitted = true;
    const { currentPassword, password, passwordConfirmation } = this.changePasswordForm.value;

    this.api.changeCurrentPassword({ currentPassword, password, passwordConfirmation }).subscribe((result: any) => {
      this.submitted = false;

      if (result?.jwt) {
        this.tokenStorage.set(new NbAuthJWTToken(result.jwt, 'email'));
      }

      this.messages = ['Your password has been changed successfully.'];

      const currentUser = this.userStore.getUser();
      if (currentUser?.id) {
        this.api.update({ id: currentUser.id, password_expired: false }).subscribe({
          error: () => { /* non-critical: password change already succeeded */ },
        });
      }

      this.toast.success(null, 'Password changed successfully', {
        icon: 'checkmark',
        limit: 3,
        position: NbGlobalLogicalPosition.TOP_END,
      });

      this.cd.detectChanges();

      setTimeout(() => {
        this.router.navigateByUrl('');
      }, this.redirectDelay ?? 1500);
    }, (errRes) => {
      this.submitted = false;
      const message = errRes?.error?.error?.message ?? errRes?.error?.message ?? 'Failed to change password.';
      this.errors = [message];
      this.toast.danger(message, 'Failed to change password', {
        icon: 'alert-triangle-outline',
        limit: 3,
        position: NbGlobalLogicalPosition.TOP_END,
      });
      this.cd.detectChanges();
    });
  }

  getConfigValue(key: string): any {
    return getDeepFromObject(this.options, key, null);
  }
}
